import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import MinorCrashIcon from '@mui/icons-material/MinorCrash'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import { GreenText, SortableTableCell } from 'components'
import * as controllers from 'controllers'
import { useEffect, useState } from 'react'
import ClusterShippersManageVehiclesAddVehiclesDialog from './ClusterShippersManageVehiclesAddVehiclesDialog'
import { enqueueSnackbar } from 'notistack'
import { useLoadingState } from 'hooks'
import { useDebounce } from 'utils'
import { DEBOUNCE_DELAY } from 'localConstants'
import { useLayout } from 'context'
import { VehicleDto, ClusterShipperDto } from 'dtos'
import { ClusterShippersParameters } from 'parameters'
import { getClusterShipperVehicles } from 'controllers'

interface ClusterShippersManageVehiclesListCardProps {
  clusterShipper: ClusterShipperDto
  setClusterShipper: React.Dispatch<React.SetStateAction<ClusterShipperDto>>
}

export default function ClusterShippersManageVehiclesListCard({
  clusterShipper,
  setClusterShipper
}: React.PropsWithChildren<ClusterShippersManageVehiclesListCardProps>) {
  const layout = useLayout()

  const [loadingState, setLoadingState] = useLoadingState({
    isGettingVehicles: false,
    isAddingVehiclesToClusterShipper: false,
    isRemovingVehiclesFromClusterShipper: false
  })

  const [clusterShippersManageVehiclesAddVehiclesDialogOpen, setClusterShippersManageVehiclesAddVehiclesDialogOpen] =
    useState<boolean>(false)

  const [parameters, setParameters] = useState<ClusterShippersParameters>({
    page: 0,
    pageSize: 10,
    order: 'asc',
    orderBy: 'vin'
  })
  const [selectedVehicles, setSelectedVehicles] = useState<VehicleDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [vehicles, setVehicles] = useState<VehicleDto[]>([])

  const getClusterShipperVehicles = useDebounce((clusterShipperGuid: string, parameters: ClusterShippersParameters) => {
    setLoadingState('isGettingVehicles', true)
    controllers
      .getClusterShipperVehicles(clusterShipperGuid, parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setVehicles(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingVehicles', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (clusterShipper && clusterShipper.clusterShipperGuid) {
      getClusterShipperVehicles(clusterShipper.clusterShipperGuid, parameters)
    }
  }, [getClusterShipperVehicles, clusterShipper, parameters])

  useEffect(() => {
    layout.setIsLayoutLoading(loadingState.isRemovingVehiclesFromClusterShipper)
  }, [loadingState.isRemovingVehiclesFromClusterShipper])

  const onChangeSortByDirection = (orderBy: string) => {
    setParameters((prev: { orderBy: string; order: string }) => ({
      ...parameters,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  return (
    <>
      <ClusterShippersManageVehiclesAddVehiclesDialog
        isLoading={loadingState.isAddingVehiclesToClusterShipper}
        clusterShipper={clusterShipper}
        onClose={() => {
          setClusterShippersManageVehiclesAddVehiclesDialogOpen(false)
        }}
        onSave={values => {
          setLoadingState('isAddingVehiclesToClusterShipper', true)
          controllers
            .addVehiclesToClusterShipper(clusterShipper.clusterShipperGuid!, values)
            .then(response => {
              enqueueSnackbar('Cluster Shipper Vehicles Saved Successfully!', { variant: 'success' })
              setClusterShippersManageVehiclesAddVehiclesDialogOpen(false)
              setClusterShipper(response.value)
              getClusterShipperVehicles(clusterShipper.clusterShipperGuid!, parameters)
            })
            .finally(() => {
              setLoadingState('isAddingVehiclesToClusterShipper', false)
            })
        }}
        open={clusterShippersManageVehiclesAddVehiclesDialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto' container spacing={1}>
                <Grid item>
                  <Typography fontWeight='bold' variant='h5'>
                    Manage Vehicles
                  </Typography>
                </Grid>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  color='error'
                  disabled={Boolean(clusterShipper.dropOffDate) || selectedVehicles.length === 0}
                  endIcon={<DeleteForeverIcon />}
                  onClick={() => {
                    setLoadingState('isRemovingVehiclesFromClusterShipper', true)
                    controllers
                      .removeVehiclesFromClusterShipper(clusterShipper.clusterShipperGuid!, selectedVehicles)
                      .then(response => {
                        setSelectedVehicles([])
                        enqueueSnackbar('Vehicles Removed Successfully!', { variant: 'success' })
                        setClusterShipper(response.value)
                      })
                      .finally(() => {
                        setLoadingState('isRemovingVehiclesFromClusterShipper', false)
                      })
                  }}
                  variant='text'
                >
                  REMOVE SELECTED
                </Button>

                <Button
                  color='primary'
                  endIcon={<MinorCrashIcon />}
                  onClick={() => {
                    setClusterShippersManageVehiclesAddVehiclesDialogOpen(true)
                  }}
                  variant='text'
                  // disabled={Boolean(clusterShipper.dropOffDate)}
                >
                  ADD VEHICLES
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {loadingState.isGettingVehicles && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!loadingState.isGettingVehicles && (
              <Grid item xs={12}>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding='checkbox'>
                          <Checkbox
                            color='primary'
                            checked={selectedVehicles.length === totalCount && selectedVehicles.length !== 0}
                            disabled={Boolean(clusterShipper.dropOffDate) || selectedVehicles.length === 0}
                            indeterminate={selectedVehicles.length > 0 && selectedVehicles.length !== totalCount}
                            onChange={() => {
                              if (selectedVehicles.length === totalCount) {
                                setSelectedVehicles([])
                              } else {
                                setSelectedVehicles(vehicles)
                              }
                            }}
                          />
                        </TableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='vin'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          VIN
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='year'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Year
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='make'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Make
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='model'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Model
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='importerStockNumber'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Importer Stock#
                        </SortableTableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {vehicles?.map((vehicle, index) => (
                        <TableRow key={vehicle.vin} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                          <TableCell padding='checkbox'>
                            <Checkbox
                              color='primary'
                              checked={selectedVehicles.some(v => v.vehicleGuid === vehicle.vehicleGuid)}
                              disabled={Boolean(clusterShipper.dropOffDate)}
                              onChange={() => {
                                if (selectedVehicles.some(v => v.vehicleGuid === vehicle.vehicleGuid)) {
                                  setSelectedVehicles(selectedVehicles.filter(v => v.vehicleGuid !== vehicle.vehicleGuid))
                                } else {
                                  setSelectedVehicles([...selectedVehicles, vehicle])
                                }
                              }}
                            />
                          </TableCell>

                          <TableCell>{vehicle.vin}</TableCell>

                          <TableCell>{vehicle.year}</TableCell>

                          <TableCell>{vehicle.make}</TableCell>

                          <TableCell>{vehicle.model}</TableCell>

                          <TableCell>{vehicle.importerStockNumber}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25]}
                          SelectProps={{ fullWidth: false }}
                          count={totalCount}
                          rowsPerPage={parameters.pageSize}
                          page={parameters.page}
                          onPageChange={(_, page) => {
                            setParameters({
                              ...parameters,
                              page
                            })
                          }}
                          onRowsPerPageChange={e => {
                            setParameters({
                              ...parameters,
                              pageSize: e.target.value as unknown as number
                            })
                          }}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Remove Selected Button:**

Clicking this button removes all selected vehicles from the Manage Vehicles Card.

This button is enabled only when at least one vehicle is selected.

**Add Vehicles Button:**

Clicking this button opens the Add Vehicles dialog.

**Select Box:**

Clicking the checkbox selects the vehicle in that row.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
